// src/common/mock/initialData.js
// Initial mock data for demo mode. Realistic data for MilyTravel app.
// Includes complex relationships: users -> receivers, combos -> currencies, timestamps, calculated fields (e.g., total price).

// Utility for timestamps (simple implementation)
export const getCurrentTimestamp = () => new Date().toISOString();

// Mock Currencies (monedas para transacciones, e.g., USD, CUP, EUR)
export const mockCurrencies = [
  {
    _id: 'curr1',
    name: 'Dólar Estadounidense',
    code: 'USD',
    symbol: '$',
    rateToCup: 24.00, // Tasa simulada
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
  },
  {
    _id: 'curr2',
    name: 'Peso Cubano',
    code: 'CUP',
    symbol: 'MN',
    rateToCup: 1.00,
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
  },
  {
    _id: 'curr3',
    name: 'Euro',
    code: 'EUR',
    symbol: '€',
    rateToCup: 26.00,
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
  },
  {
    _id: 'curr4',
    name: 'Euro',
    code: 'EUR',
    symbol: '€',
    rateToCup: 25.50,
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
  },
  {
    _id: 'curr5',
    name: 'Yen Japonés',
    code: 'JPY',
    symbol: '¥',
    rateToCup: 0.16,
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
  },
];

// Mock Users (usuarios con roles: admin, user; relaciones con receivers)
export const mockUsers = [
  {
    _id: 'user1',
    email: 'admin@milytravel.com',
    password: 'hashed_admin_pass', // In demo, any login works
    role: 'admin',
    fullName: 'Admin Usuario',
    phone: '+53 555 1234',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isVerified: true,
    receivers: ['recv1', 'recv2'], // Relación many-to-many via IDs
    balance: { USD: 1000, CUP: 24000 }, // Calculated field
  },
  {
    _id: 'user2',
    email: 'cliente1@example.com',
    password: 'hashed_pass1',
    role: 'user',
    fullName: 'Juan Pérez',
    phone: '+53 555 5678',
    createdAt: new Date('2024-01-15T10:00:00Z').toISOString(),
    updatedAt: getCurrentTimestamp(),
    isVerified: true,
    receivers: ['recv3'],
    balance: { USD: 500, CUP: 12000 },
  },
  {
    _id: 'user3',
    email: 'cliente2@example.com',
    password: 'hashed_pass2',
    role: 'user',
    fullName: 'María García',
    phone: '+53 555 9012',
    createdAt: new Date('2024-02-20T14:30:00Z').toISOString(),
    updatedAt: getCurrentTimestamp(),
    isVerified: false, // For demo verification flow
    receivers: ['recv4', 'recv5'],
    balance: { USD: 200, CUP: 4800 },
  },
  // Add more for complexity
  {
    _id: 'user4',
    email: 'admin2@milytravel.com',
    password: 'hashed_admin2',
    role: 'admin',
    fullName: 'Admin Dos',
    phone: '+53 555 3456',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isVerified: true,
    receivers: [],
    balance: { USD: 0, CUP: 0 },
  },
  {
    _id: 'user5',
    email: 'cliente3@example.com',
    password: 'hashed_pass3',
    role: 'user',
    fullName: 'Carlos López',
    phone: '+53 555 7890',
    createdAt: new Date('2024-03-10T09:15:00Z').toISOString(),
    updatedAt: getCurrentTimestamp(),
    isVerified: true,
    receivers: ['recv6'],
    balance: { USD: 300, CUP: 7200 },
  },
  {
    _id: 'user6',
    email: 'cliente4@example.com',
    password: 'hashed_pass4',
    role: 'user',
    fullName: 'Ana Rodríguez',
    phone: '+53 555 2345',
    createdAt: new Date('2024-03-15T16:45:00Z').toISOString(),
    updatedAt: getCurrentTimestamp(),
    isVerified: true,
    receivers: ['recv7', 'recv8'],
    balance: { USD: 750, CUP: 18000 },
  },
];

// Mock Receivers (receptores para envíos, relacionados con users)
export const mockReceivers = [
  {
    _id: 'recv1',
    userId: 'user1', // Foreign key to user
    fullName: 'Receptor Admin',
    email: 'receptor1@cuba.com',
    phone: '+53 555 1111',
    address: {
      street: 'Calle 1 #123',
      city: 'La Habana',
      province: 'La Habana',
      township: 'Vedado',
    },
    preferredCurrency: 'curr2', // Relación con currency
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
  },
  {
    _id: 'recv2',
    userId: 'user1',
    fullName: 'Otro Receptor',
    email: 'receptor2@cuba.com',
    phone: '+53 555 2222',
    address: {
      street: 'Avenida 2 #456',
      city: 'Santiago de Cuba',
      province: 'Santiago',
      township: 'Centro',
    },
    preferredCurrency: 'curr1',
    createdAt: new Date('2024-03-10T09:00:00Z').toISOString(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
  },
  {
    _id: 'recv3',
    userId: 'user2',
    fullName: 'Receptor Juan',
    email: 'juanreceptor@cuba.com',
    phone: '+53 555 3333',
    address: {
      street: 'Calle 3 #789',
      city: 'Matanzas',
      province: 'Matanzas',
      township: 'Varadero',
    },
    preferredCurrency: 'curr3',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
  },
  // Add more
  {
    _id: 'recv4',
    userId: 'user3',
    fullName: 'Receptor María 1',
    email: 'maria1@cuba.com',
    phone: '+53 555 4444',
    address: {
      street: 'Calle 4 #101',
      city: 'Pinar del Río',
      province: 'Pinar',
      township: 'Centro',
    },
    preferredCurrency: 'curr2',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
  },
  {
    _id: 'recv5',
    userId: 'user3',
    fullName: 'Receptor María 2',
    email: 'maria2@cuba.com',
    phone: '+53 555 5555',
    address: {
      street: 'Avenida 5 #112',
      city: 'Camagüey',
      province: 'Camagüey',
      township: 'Sur',
    },
    preferredCurrency: 'curr1',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: false, // For demo delete flow
  },
  {
    _id: 'recv6',
    userId: 'user5',
    fullName: 'Receptor Carlos',
    email: 'carlosreceptor@cuba.com',
    phone: '+53 555 6666',
    address: {
      street: 'Calle 6 #456',
      city: 'Holguín',
      province: 'Holguín',
      township: 'Centro',
    },
    preferredCurrency: 'curr4',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
  },
  {
    _id: 'recv7',
    userId: 'user6',
    fullName: 'Receptor Ana 1',
    email: 'ana1@cuba.com',
    phone: '+53 555 7777',
    address: {
      street: 'Avenida 7 #789',
      city: 'Villa Clara',
      province: 'Villa Clara',
      township: 'Santa Clara',
    },
    preferredCurrency: 'curr5',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
  },
  {
    _id: 'recv8',
    userId: 'user6',
    fullName: 'Receptor Ana 2',
    email: 'ana2@cuba.com',
    phone: '+53 555 8888',
    address: {
      street: 'Calle 8 #101',
      city: 'Ciego de Ávila',
      province: 'Ciego de Ávila',
      township: 'Morón',
    },
    preferredCurrency: 'curr3',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
  },
];

// Mock Combos (paquetes de comida, con precios en currencies, imágenes)
export const mockCombos = [
  
  {
    _id: 'combo5',
    name: 'Combo Premium Pollo',
    description: 'Pollo premium con guarniciones especiales.',
    price: 22.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-1.jpg',
    provinceAvailability: ['La Habana', 'Santiago de Cuba', 'Matanzas'],
    items: ['Pollo premium (600g)', 'Arroz con vegetales', 'Yuca frita', 'Ensalada mixta'],
    deliveryFee: 2.50,
    totalPrice: 24.50,
    stock: 35,
    category: 'Premium',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user1', rating: 5, comment: 'Muy sabroso y fresco.' },
      { userId: 'user2', rating: 4, comment: 'Porción generosa.' }
    ],
  },
  {
    _id: 'combo6',
    name: 'Combo Familiar Res',
    description: 'Para familia, res asada con acompañamientos.',
    price: 30.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-2.jpg',
    provinceAvailability: ['La Habana', 'Pinar del Río', 'Villa Clara'],
    items: ['Res asada (800g)', 'Frijoles negros', 'Plátano maduro', 'Arroz blanco', 'Ensalada'],
    deliveryFee: 3.00,
    totalPrice: 33.00,
    stock: 45,
    category: 'Familiar',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user3', rating: 5, comment: 'Perfecto para compartir.' }
    ],
  },
  {
    _id: 'combo7',
    name: 'Combo Básico Mixto',
    description: 'Mix básico de pollo y cerdo.',
    price: 16.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-3.jpg',
    provinceAvailability: ['Matanzas', 'Cienfuegos', 'Sancti Spíritus'],
    items: ['Pollo (300g)', 'Cerdo (200g)', 'Arroz', 'Verduras'],
    deliveryFee: 1.50,
    totalPrice: 17.50,
    stock: 55,
    category: 'Básico',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user4', rating: 4, comment: 'Buena opción económica.' },
      { userId: 'user2', rating: 5, comment: 'Rápido y rico.' }
    ],
  },
  {
    _id: 'combo8',
    name: 'Combo Especial Vegetariano',
    description: 'Opciones vegetarianas con vegetales frescos.',
    price: 14.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-4.jpg',
    provinceAvailability: ['Camagüey', 'Las Tunas', 'Granma'],
    items: ['Vegetales mixtos (1kg)', 'Arroz integral', 'Frijoles', 'Ensalada grande'],
    deliveryFee: 2.00,
    totalPrice: 16.00,
    stock: 20,
    category: 'Especial',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user1', rating: 5, comment: 'Saludable y delicioso.' }
    ],
  },
  {
    _id: 'combo9',
    name: 'Combo Mixto Premium',
    description: 'Mixto de alta calidad con extras.',
    price: 28.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-5.jpg',
    provinceAvailability: ['Holguín', 'Guantánamo'],
    items: ['Pollo y res (500g cada)', 'Aceite de oliva', 'Vegetales orgánicos', 'Arroz', 'Salsa especial'],
    deliveryFee: 3.50,
    totalPrice: 31.50,
    stock: 28,
    category: 'Premium',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user3', rating: 5, comment: 'Calidad superior.' },
      { userId: 'user4', rating: 4, comment: 'Vale cada centavo.' }
    ],
  },
  {
    _id: 'combo10',
    name: 'Combo Cerdo Frito',
    description: 'Cerdo frito crujiente con guarniciones.',
    price: 19.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-6.jpg',
    provinceAvailability: ['La Habana', 'Santiago de Cuba', 'Ciego de Ávila'],
    items: ['Cerdo frito (700g)', 'Papas fritas', 'Ensalada', 'Plátano'],
    deliveryFee: 2.00,
    totalPrice: 21.00,
    stock: 40,
    category: 'Mixto',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [],
  },
  {
    _id: 'combo11',
    name: 'Combo Básico Res',
    description: 'Res básica con arroz y vegetales.',
    price: 17.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-7.jpg',
    items: ['Res (400g)', 'Arroz (400g)', 'Verduras al vapor'],
    deliveryFee: 1.80,
    totalPrice: 18.80,
    stock: 60,
    category: 'Básico',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user2', rating: 4, comment: 'Sencillo pero bueno.' }
    ],
  },
  {
    _id: 'combo12',
    name: 'Combo Familiar Pollo',
    description: 'Pollo para toda la familia.',
    price: 32.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-8.jpg',
    items: ['Pollo entero asado', 'Arroz', 'Frijoles', 'Yuca', 'Ensalada', 'Pan'],
    deliveryFee: 4.00,
    totalPrice: 36.00,
    stock: 25,
    category: 'Familiar',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user1', rating: 5, comment: 'Ideal para cenas familiares.' },
      { userId: 'user3', rating: 5, comment: 'Porciones grandes.' }
    ],
  },
  {
    _id: 'combo13',
    name: 'Combo Especial Mixto 2',
    description: 'Mix especial con mariscos simulados.',
    price: 35.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-9.jpg',
    items: ['Pollo y cerdo (600g)', 'Camarones simulados', 'Arroz con azafrán', 'Vegetales'],
    deliveryFee: 3.20,
    totalPrice: 38.20,
    stock: 18,
    category: 'Especial',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user4', rating: 5, comment: 'Exótico y delicioso.' }
    ],
  },
  {
    _id: 'combo14',
    name: 'Combo Premium Vegetal',
    description: 'Vegetales premium orgánicos.',
    price: 20.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-10.jpg',
    items: ['Vegetales orgánicos (1.2kg)', 'Quinoa', 'Ensalada especial', 'Salsas naturales'],
    deliveryFee: 2.50,
    totalPrice: 22.50,
    stock: 30,
    category: 'Premium',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user2', rating: 4, comment: 'Saludable opción.' },
      { userId: 'user3', rating: 5, comment: 'Frescos y variados.' }
    ],
  },
  {
    _id: 'combo15',
    name: 'Combo Mixto Cerdo',
    description: 'Cerdo mixto con extras.',
    price: 24.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-11.jpg',
    items: ['Cerdo (500g)', 'Pollo (200g)', 'Frijoles', 'Arroz', 'Plátano frito'],
    deliveryFee: 2.80,
    totalPrice: 26.80,
    stock: 42,
    category: 'Mixto',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [],
  },
  {
    _id: 'combo16',
    name: 'Combo Básico Especial',
    description: 'Básico con toque especial.',
    price: 13.50,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-12.jpg',
    items: ['Pollo básico (400g)', 'Arroz', 'Ensalada', 'Salsa'],
    deliveryFee: 1.50,
    totalPrice: 15.00,
    stock: 50,
    category: 'Básico',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user1', rating: 4, comment: 'Simple y efectivo.' }
    ],
  },
  {
    _id: 'combo17',
    name: 'Combo Familiar Premium',
    description: 'Premium para familia grande.',
    price: 40.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-13.jpg',
    items: ['Res y pollo (1kg total)', 'Arroz grande', 'Frijoles', 'Yuca', 'Vegetales', 'Postre'],
    deliveryFee: 4.50,
    totalPrice: 44.50,
    stock: 22,
    category: 'Familiar',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user4', rating: 5, comment: 'Lujo en casa.' },
      { userId: 'user2', rating: 5, comment: 'Recomendado.' }
    ],
  },
  {
    _id: 'combo18',
    name: 'Combo Especial Res',
    description: 'Res especial marinada.',
    price: 26.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-14.jpg',
    items: ['Res marinada (600g)', 'Papas al horno', 'Ensalada', 'Salsa BBQ'],
    deliveryFee: 3.00,
    totalPrice: 29.00,
    stock: 35,
    category: 'Especial',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user3', rating: 4, comment: 'Sabor único.' }
    ],
  },
  {
    _id: 'combo19',
    name: 'Combo Mixto Básico 2',
    description: 'Otro mix básico variado.',
    price: 15.50,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-15.jpg',
    items: ['Cerdo (300g)', 'Verduras', 'Arroz', 'Frijoles'],
    deliveryFee: 2.00,
    totalPrice: 17.50,
    stock: 48,
    category: 'Mixto',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user1', rating: 5, comment: 'Equilibrado.' }
    ],
  },
  {
    _id: 'combo20',
    name: 'Combo Premium Familiar',
    description: 'Familiar con ingredientes premium.',
    price: 38.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-16.jpg',
    items: ['Pollo y res premium', 'Quinoa', 'Vegetales orgánicos', 'Ensalada', 'Fruta'],
    deliveryFee: 4.00,
    totalPrice: 42.00,
    stock: 30,
    category: 'Premium',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user2', rating: 5, comment: 'Alta calidad familiar.' },
      { userId: 'user4', rating: 4, comment: 'Delicioso conjunto.' }
    ],
  },
  {
    _id: 'combo21',
    name: 'Combo Pollo Mixto',
    description: 'Pollo con mix de vegetales.',
    price: 21.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-17.jpg',
    items: ['Pollo (500g)', 'Mix vegetales', 'Arroz', 'Yuca'],
    deliveryFee: 2.20,
    totalPrice: 23.20,
    stock: 55,
    category: 'Básico',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [],
  },
  {
    _id: 'combo22',
    name: 'Combo Especial Cerdo',
    description: 'Cerdo especial con salsas.',
    price: 23.50,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-18.jpg',
    items: ['Cerdo especial (600g)', 'Papas', 'Salsas variadas', 'Ensalada'],
    deliveryFee: 2.80,
    totalPrice: 26.30,
    stock: 28,
    category: 'Especial',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user3', rating: 5, comment: 'Sabor auténtico.' }
    ],
  },
  {
    _id: 'combo23',
    name: 'Combo Mixto Res Premium',
    description: 'Res premium en mix.',
    price: 29.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-19.jpg',
    items: ['Res premium (500g)', 'Pollo (300g)', 'Vegetales', 'Arroz especial'],
    deliveryFee: 3.50,
    totalPrice: 32.50,
    stock: 32,
    category: 'Premium',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user1', rating: 5, comment: 'Excelente combinación.' },
      { userId: 'user2', rating: 4, comment: 'Bien equilibrado.' }
    ],
  },
  {
    _id: 'combo24',
    name: 'Combo Final Básico',
    description: 'Básico completo para cierre.',
    price: 12.00,
    currencyId: 'curr1',
    image: '/mock-combos/mock-combo-20.jpg',
    items: ['Arroz y frijoles', 'Pollo simple (400g)', 'Ensalada'],
    deliveryFee: 1.50,
    totalPrice: 13.50,
    stock: 65,
    category: 'Básico',
    createdAt: getCurrentTimestamp(),
    updatedAt: getCurrentTimestamp(),
    isActive: true,
    reviews: [
      { userId: 'user4', rating: 4, comment: 'Opción ligera.' }
    ],
  },
];

// Export all for easy import
export const initialMockData = {
  users: mockUsers,
  receivers: mockReceivers,
  currencies: mockCurrencies,
  combos: mockCombos,
};